/**
 * Carrousel d'avis illustrés DOSÉ.
 *
 * Trois rôles :
 *   1. poser les libellés réglés sur le conteneur dans chaque carte ;
 *   2. n'afficher « en savoir plus » que si l'avis dépasse réellement, ce que
 *      le CSS seul ne sait pas mesurer ;
 *   3. faire avancer la bande en continu, en boucle, sans à-coup.
 *
 * L'apparition du texte au survol est entièrement en CSS : elle continue de
 * fonctionner même si ce script ne se charge pas.
 */

// Délai avant que la bande reparte après un geste de la personne.
const REPRISE_MS = 2500;

class DoseUgcReviews extends HTMLElement {
  #controller = new AbortController();
  #resize = null;
  #visibilite = null;

  #rafId = 0;
  #dernierTemps = 0;
  #reprise = 0;
  #enPause = false;
  #cloneFait = false;
  #reste = 0;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#poserLibelles();
    this.#brancherDepliage(signal);

    this.#mesurer();
    this.#resize = new ResizeObserver(() => this.#mesurer());
    this.#resize.observe(this);
    document.fonts?.ready.then(() => this.#mesurer());

    if (this.hasAttribute('autoscroll')) this.#demarrerDefilement(signal);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resize?.disconnect();
    this.#visibilite?.disconnect();
    cancelAnimationFrame(this.#rafId);
  }

  get #cartes() {
    return Array.from(this.querySelectorAll('.ugc-review'));
  }

  get #piste() {
    return this.querySelector('[data-ugc-track]');
  }

  /* ------------------------------------------------------------- Libellés */

  #poserLibelles() {
    const verifie = this.getAttribute('verified-label');
    const plus = this.getAttribute('more-label');

    if (verifie) {
      this.querySelectorAll('[data-ugc-verified-label]').forEach((n) => {
        n.textContent = verifie;
      });
    }
    if (plus) {
      this.querySelectorAll('[data-ugc-more]').forEach((n) => {
        n.textContent = plus;
      });
    }
  }

  /* ------------------------------------------------------------ Dépliage */

  /**
   * Le lien n'apparaît que si le texte est réellement tronqué. Comparer la
   * hauteur du contenu à la hauteur visible est la seule mesure fiable :
   * compter les caractères donnerait un résultat faux dès qu'on change de
   * police ou de largeur.
   */
  #mesurer() {
    this.#cartes.forEach((carte) => {
      const texte = carte.querySelector('[data-ugc-text]');
      const bouton = carte.querySelector('[data-ugc-more]');
      const voile = carte.querySelector('[data-ugc-overlay]');
      if (!texte || !bouton || voile?.classList.contains('ugc-review__overlay--open')) return;

      bouton.hidden = texte.scrollHeight <= texte.clientHeight + 1;
    });
  }

  #brancherDepliage(signal) {
    this.addEventListener(
      'click',
      (event) => {
        const bouton = event.target.closest('[data-ugc-more]');
        if (!bouton) return;

        const voile = bouton.closest('[data-ugc-overlay]');
        voile?.classList.add('ugc-review__overlay--open');
        bouton.hidden = true;
      },
      { signal }
    );

    // Quitter la photo referme l'avis : on retrouve l'état d'origine sans
    // avoir à cliquer quoi que ce soit.
    this.addEventListener(
      'mouseleave',
      (event) => {
        const media = event.target.closest?.('[data-ugc-media]');
        if (!media) return;

        media.querySelector('[data-ugc-overlay]')?.classList.remove('ugc-review__overlay--open');
        this.#mesurer();
      },
      { capture: true, signal }
    );
  }

  /* -------------------------------------------------- Défilement continu */

  /**
   * Duplique la série une fois. La boucle devient alors invisible : quand on
   * atteint la moitié du contenu, on revient au début sans que rien ne bouge
   * à l'écran, puisque les deux moitiés sont identiques.
   */
  #cloner() {
    const piste = this.#piste;
    if (!piste || this.#cloneFait) return;
    if (piste.scrollWidth <= piste.clientWidth + 1) return;

    this.#cartes.forEach((carte) => {
      const copie = carte.cloneNode(true);
      copie.setAttribute('aria-hidden', 'true');
      copie.dataset.clone = 'true';
      piste.appendChild(copie);
    });
    this.#cloneFait = true;
    this.#poserLibelles();
    this.#mesurer();
  }

  #demarrerDefilement(signal) {
    // Une bande qui bouge sans arrêt est une gêne pour qui a réglé son système
    // sur « moins d'animations ». On respecte ce choix.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#cloner();
    if (!this.#cloneFait) return;

    if (this.hasAttribute('pause-on-hover')) {
      this.addEventListener('pointerenter', () => (this.#enPause = true), { signal });
      this.addEventListener('pointerleave', () => (this.#enPause = false), { signal });
    }

    // Un geste de la personne reprend la main : on s'efface, puis on repart.
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
      this.#piste?.addEventListener(type, () => this.#suspendre(), { signal, passive: true });
    });
    this.#piste?.addEventListener('scroll', () => this.#boucler(), { signal, passive: true });

    // Inutile d'animer une bande hors de l'écran.
    this.#visibilite = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (e.isIntersecting) this.#reprendre();
        else this.#arreter();
      });
    });
    this.#visibilite.observe(this);
  }

  #suspendre() {
    this.#reprise = performance.now() + REPRISE_MS;
  }

  #reprendre() {
    if (this.#rafId) return;
    this.#dernierTemps = 0;
    this.#rafId = requestAnimationFrame((t) => this.#avancer(t));
  }

  #arreter() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  /**
   * Ramène la position dans la première moitié, celle qui a un jumeau à
   * l'identique dans la seconde : le retour au début ne se voit pas.
   * Uniquement vers l'avant — replier aussi à zéro ferait sauter la bande au
   * milieu dès le chargement, puisque la position de départ est justement zéro.
   */
  #boucler() {
    const piste = this.#piste;
    if (!piste) return;

    const moitie = piste.scrollWidth / 2;
    if (moitie > 0 && piste.scrollLeft >= moitie) piste.scrollLeft -= moitie;
  }

  #avancer(temps) {
    const piste = this.#piste;
    if (!piste) return;

    const ecoule = this.#dernierTemps ? temps - this.#dernierTemps : 0;
    this.#dernierTemps = temps;

    const enAttente = temps < this.#reprise;
    if (!this.#enPause && !enAttente && ecoule > 0) {
      const vitesse = Number(this.getAttribute('speed')) || 30;

      // Certains navigateurs arrondissent `scrollLeft` à l'entier. On garde
      // donc la fraction de côté, sinon une vitesse lente ne ferait jamais
      // avancer la bande : chaque pas serait perdu à l'arrondi.
      this.#reste += (vitesse * ecoule) / 1000;
      const pas = Math.floor(this.#reste);
      if (pas >= 1) {
        this.#reste -= pas;
        piste.scrollLeft += pas;
        this.#boucler();
      }
    }

    this.#rafId = requestAnimationFrame((t) => this.#avancer(t));
  }
}

if (!customElements.get('dose-ugc-reviews')) {
  customElements.define('dose-ugc-reviews', DoseUgcReviews);
}
